import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  NOTIFICATIONS_MAX_LIMIT,
  NOTIFICATIONS_PAGE,
  type Notification,
  type NotificationKind,
  notificationsQueryOptions,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from '@/entities/notification'
import { cn, fmtAgo, fmtFullDate, fmtTime, momentZone, zoneTitle } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon, type IconName } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { ZoneTag } from '@/shared/ui/zone-tag'

export const Route = createFileRoute('/_app/inbox')({
  component: InboxPage,
})

// Вид алерта → иконка и тон. Тон тихий, заливок нет: акцент в ленте тратится
// только на потерю события (отмена/удаление) и на бейдж непрочитанных.
const KIND_META: Record<NotificationKind, { icon: IconName; tone: string }> = {
  'event-invited': { icon: 'user-plus', tone: 'text-text-secondary' },
  'event-uninvited': { icon: 'log-out', tone: 'text-text-tertiary' },
  'event-rescheduled': { icon: 'clock', tone: 'text-info' },
  'event-cancelled': { icon: 'close', tone: 'text-accent' },
  'event-deleted': { icon: 'trash', tone: 'text-accent' },
  'event-reminder': { icon: 'bell', tone: 'text-warning' },
}

const rowClass =
  'flex w-full gap-3 rounded-control px-2.5 py-2.5 text-left transition-colors hover:bg-bg-raised'

const SKELETON = ['s1', 's2', 's3', 's4'] as const

/**
 * Полный момент записи в титуле строки: дата, время и подпись зоны — в одной
 * зоне устройства. Половины в разных зонах разъезжались бы на сутки у полуночи.
 */
function fullMoment(at: Date): string {
  const tz = momentZone()
  return `${fmtFullDate(at, tz)}, ${fmtTime(at, tz)} · ${zoneTitle(tz)}`
}

interface RowProps {
  notification: Notification
  /** Открыть = пометить прочитанным; переход берёт на себя Link. */
  onOpen: () => void
}

function NotificationRow({ notification: n, onOpen }: RowProps) {
  const meta = KIND_META[n.kind]
  const unread = !n.readAt

  const body = (
    <>
      <span className="mt-1.5 shrink-0">
        <span
          aria-hidden
          className={cn(
            'block size-2 rounded-pill',
            unread ? 'bg-accent' : 'border border-border-strong',
          )}
        />
        {unread && <span className="sr-only">Не прочитано</span>}
      </span>
      <Icon name={meta.icon} size={16} className={cn('mt-0.5 shrink-0', meta.tone)} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              unread ? 'text-text-primary' : 'text-text-secondary',
            )}
          >
            {n.title}
          </span>
          {/* Давность («12 мин назад») зоной не подписывается — она одинакова в
              любой зоне. Абсолютную дату старой записи подписывает заголовок
              колонки: тег в каждой строке превратил бы ленту в частокол. */}
          <time
            className="mono-num shrink-0 text-text-tertiary text-xs"
            dateTime={n.createdAt.toISOString()}
            title={fullMoment(n.createdAt)}
          >
            {fmtAgo(n.createdAt)}
          </time>
        </span>
        {n.body && (
          <span className="mt-0.5 block truncate text-sm text-text-secondary">{n.body}</span>
        )}
        {n.context && <span className="mt-0.5 block text-text-tertiary text-xs">{n.context}</span>}
      </span>
    </>
  )

  // Событие удалено — открывать нечего (eventId бэк не присылает), строка
  // остаётся кнопкой «прочитано».
  return (
    <li>
      {n.eventId ? (
        <Link
          to="/events/$eventId"
          params={{ eventId: n.eventId }}
          onClick={onOpen}
          className={rowClass}
        >
          {body}
        </Link>
      ) : (
        <button type="button" onClick={onOpen} className={rowClass}>
          {body}
        </button>
      )}
    </li>
  )
}

// Лента алертов сотрудника: что случилось с его событиями. Раздел
// сотруднический — у заказчика identity в хабе нет, и ленты у него не бывает
// вовсе (гейт контура — в _app).
function InboxPage() {
  const [limit, setLimit] = useState(NOTIFICATIONS_PAGE)
  // keepPreviousData: «показать ещё» не должно моргать скелетоном уже
  // прочитанного куска ленты.
  const list = useQuery({
    ...notificationsQueryOptions(limit),
    placeholderData: keepPreviousData,
  })
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()

  const items = list.data ?? []
  const open = (n: Notification) => {
    if (!n.readAt) markRead.mutate(n.id)
  }
  // Кнопки нет, когда гасить нечего: ряд действий не занимает место ради
  // операции, которая ничего не изменит.
  const hasUnread = items.some((n) => !n.readAt)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title="Уведомления"
        description="Что произошло с вашими событиями: приглашения, переносы, отмены и напоминания."
        actions={
          hasUnread && (
            <Button
              variant="secondary"
              size="sm"
              disabled={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              Прочитать все
            </Button>
          )
        }
      />

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-gutter pb-safe-8">
        <div className="max-w-2xl">
          {list.isPending ? (
            <ul className="flex flex-col gap-1" aria-hidden>
              {SKELETON.map((key) => (
                <li key={key} className="flex gap-3 px-2.5 py-2.5">
                  <span className="mt-1.5 size-2 shrink-0 rounded-pill bg-bg-elevated" />
                  <span className="mt-0.5 size-4 shrink-0 rounded-control bg-bg-elevated" />
                  <span className="flex-1">
                    <span className="block h-3 w-3/5 rounded-control bg-bg-elevated" />
                    <span className="mt-2 block h-3 w-2/5 rounded-control bg-bg-elevated" />
                  </span>
                </li>
              ))}
            </ul>
          ) : list.isError ? (
            <div className="flex flex-col items-start gap-3 px-1 py-8">
              <p className="text-sm text-text-tertiary">Ленту не удалось загрузить.</p>
              <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
                Повторить
              </Button>
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="Уведомлений нет"
              description="Здесь появятся приглашения, переносы и отмены ваших событий."
            />
          ) : (
            <>
              {/* Заголовок колонки времени: зона названа один раз на всю ленту.
                  Строки давностью зону не несут, но у старых записей время
                  становится датой — и та читается в той же зоне устройства,
                  что и остальные моменты ленты. */}
              <div className="flex justify-end px-2.5 pb-1">
                <span className="label text-[10px]">
                  {/* normal-case: имя зоны — не заголовок, а значение; версалом
                      «МОСКВА» оно перестаёт совпадать с тегом в остальном UI. */}
                  время · <ZoneTag className="inline text-[10px] normal-case" />
                </span>
              </div>
              <ul className="flex flex-col gap-1">
                {items.map((n) => (
                  <NotificationRow key={n.id} notification={n} onOpen={() => open(n)} />
                ))}
              </ul>
              {/* Полная страница — значит за ней может быть ещё: курсора у ленты
                  нет, страница растёт тем же запросом. На потолке страницы бэка
                  кнопку убираем — расти дальше ей некуда. */}
              {items.length >= limit && limit < NOTIFICATIONS_MAX_LIMIT && (
                <div className="flex justify-center py-4">
                  {list.isFetching ? (
                    <Spinner label="Загружаем ещё" />
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setLimit((l) => l + NOTIFICATIONS_PAGE)}
                    >
                      Показать ещё
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
